import { useSiteStore } from "@/store/siteStore";
import moment, { defaultFormat, now } from "moment";
import { DateUnit } from "~/enums";

class CalendarUtils {
	static dateType: string = "day";
	static dateFormat: string = "YYYY-MM-DD";

	static setDateType(value: number) {
		if (value === 0) {
			this.dateType = "day";
		} else if (value === 1) {
			this.dateType = "month";
		} else if (value === 2) {
			this.dateType = "year";
		}

		this.setDefaultDateFormat(this.dateType);
	}

	static setDefaultDateFormat(dateType: string) {
		if (dateType === "day") this.dateFormat = "YYYY-MM-DD";
		else if (dateType === "month") this.dateFormat = "YYYY-MM";
		else if (dateType === "year") this.dateFormat = "YYYY";
	}

	static removeDate(date: string) {
		let nowDate;

		if (this.dateType === "day") {
			nowDate = moment(date).add(-1, 'day').format('YYYY-MM-DD HH:mm:ss');
		} else if (this.dateType === "month") {
			nowDate = moment(date).add(-1, 'month').format('YYYY-MM-DD HH:mm:ss');
		} else if (this.dateType === "year") {
			nowDate = moment(date).add(-1, 'year').format('YYYY-MM-DD HH:mm:ss');
		}
		return this.setDateFormat(nowDate as string);
	}

	static setDate() {
		let labels = [] as string[];
		const dataDate = new Date(useSiteStore().siteData.pv.day[0].date);

		if (this.dateType === "day") {
			labels = useSiteStore().siteData.pv.day.map((v) => v.date);
		} else if (this.dateType === "month") {
			const days = dataDate.getDate();
			labels = [];
			for (let i = 0; i < days; i++) {
				labels.push((i + 1).toString() + "일");
			}
		} else if (this.dateType === "year") {
			const months = dataDate.getMonth();

			for (let i = 0; i < 12; i++) {
				labels.push((i + 1).toString() + "월");
			}
		}
		return labels;
	}

	static setStartDate(date: string) {
		let nowDate = moment(date, this.dateFormat).format("YYYY-MM-DD");

    let startDate = null;
		if (this.dateType === "day") {
			startDate = moment(nowDate, this.dateFormat).startOf(DateUnit.Day).format("YYYY-MM-DD");
		} else if (this.dateType === "month") {
			startDate = moment(nowDate, this.dateFormat).startOf(DateUnit.Month).format("YYYY-MM-DD");
		} else if (this.dateType === "year") {
			startDate = moment(nowDate, this.dateFormat).startOf(DateUnit.Year).format("YYYY-MM-DD");
		}


		return startDate as string;
	}

	static setEndDate(date: string) {
		let nowDate = moment(date, this.dateFormat).format("YYYY-MM-DD");

		let endDate = null;
		if (this.dateType === "day") {
			endDate = moment(nowDate, this.dateFormat).endOf(DateUnit.Day).format("YYYY-MM-DD");
		} else if (this.dateType === "month") {
			endDate = moment(nowDate, this.dateFormat).endOf(DateUnit.Month).format("YYYY-MM-DD");
		} else if (this.dateType === "year") {
			endDate = moment(nowDate, this.dateFormat).endOf(DateUnit.Year).format("YYYY-MM-DD");
		}

		return endDate as string;
	}

	static setStartEndDate(startDate: string, endDate: string) {
		let format = "YYYY-MM-DD";

		switch (this.dateType) {
			case "day":
				format = "YYYY-MM-DD";
				break;
			case "month":
				format = "YYYY-MM";
				break;
			case "year":
				format = "YYYY";
				break;
		}


		let start = moment(startDate)
			.startOf(this.dateType as moment.unitOfTime.StartOf)
			.format(format);
		let end = moment(endDate)
			.endOf(this.dateType as moment.unitOfTime.StartOf)
			.format(format);
      

		return { start, end };
	}

	static addDate(date: string) {
		const nowDate = new Date(date);
		if (this.dateType === "day") {
			nowDate.setDate(nowDate.getDate() + 1);
		} else if (this.dateType === "month") {
			nowDate.setMonth(nowDate.getMonth() + 1);
		} else if (this.dateType === "year") {
			nowDate.setFullYear(nowDate.getFullYear() + 1);
		}

		return this.setDateFormat(nowDate.toString());
	}

	static setDateFormat(date: string, dateType: string | undefined = undefined) {
		let selectDateType = this.dateType;

		if (dateType) {
			selectDateType = dateType;
		}
		let defaultFormat = moment(date).format("YYYY-MM-DD");

		if (selectDateType === "day") defaultFormat = moment(date).format("YYYY-MM-DD");
		else if (selectDateType === "month") defaultFormat = moment(date).format("YYYY-MM");
		else if (selectDateType === "year") defaultFormat = moment(date).format("YYYY");

		return defaultFormat;
	}

	static setDataFull(data: number[][], dateType: string | number, month: string | number | undefined = undefined) {
		let result = [];

		result.push([] as number[]);
		result.push([] as number[]);

		if (dateType === 0 || dateType === "day") {
			for (let i = 0; i < 24; i++) {
				result[0].push(data[0][i] ?? null);
				result[1].push(data[1][i] ?? null);
			}
		} else if (dateType === 1 || dateType === "month") {
			let endDay = moment(month).daysInMonth();
			for (let i = 0; i < Number(endDay); i++) {
				result[0].push(data[0][i] ?? null);
				result[1].push(data[1][i] ?? null);
			}
		} else if (dateType === 2 || dateType === "year") {
			for (let i = 0; i < 12; i++) {
				result[0].push(data[0][i] ?? null);
				result[1].push(data[1][i] ?? null);
			}
		}

		return result;
	}

	static setLabelsFull(label: string[], dateType: string | number, month: string | number | undefined = undefined) {
		let result = [];

		if (dateType === 0 || dateType === "day") {
			for (let i = 0; i < 24; i++) {
				let hour = i < 10 ? `0${i}H` : `${i}H`;
				result.push(hour);
			}
		} else if (dateType === 1 || dateType === "month") {
			let endDay = moment(month).daysInMonth();
			for (let i = 0; i < Number(endDay); i++) {
				let day = i < 10 ? `0${i}D` : `${i}D`;
				result.push(day);
			}
		} else if (dateType === 2 || dateType === "year") {
			for (let i = 0; i < 12; i++) {
				let month = i < 10 ? `0${i + 1}M` : `${i + 1}M`;
				result.push(month);
			}
		}

		return result;
	}

	static isTodayOrPast(date: string) {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		if (this.dateType === "year") {
			const selectedYear = new Date(date).getFullYear();
			const currentYear = today.getFullYear();

			return selectedYear < currentYear;
		} else if (this.dateType === "month") {
			const selectedMonth = Number(moment(date).format("X"));
			const currentMonth = Number(moment(today).startOf(DateUnit.Month).format("X"));
			return selectedMonth < currentMonth;
		} else {
			const selectedDate = new Date(date);
			selectedDate.setHours(0, 0, 0, 0);

			return selectedDate < today;
		}
	}
}

export default CalendarUtils;
